if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Every submit starts from a clean error state.
        //
        // Without this the component LATCHES. The failure path below does
        //   const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        //   if (!soldOutMessage) return;
        // and that early return skips `this.error = true`, so `this.error` keeps whatever
        // a PREVIOUS submit left on it. `.finally()` only clears `aria-disabled` when
        // `!this.error`, so once anything set it true the button stays aria-disabled and
        // every later tap dies on the guard one line above. Silently: no error, no spinner,
        // no add. That is one of the three mechanisms behind the 31 sessions that
        // dead-clicked Add to bag and logged zero AddToCart for the whole session.
        //
        // Deliberately placed AFTER the guard, so a genuine double-tap during an
        // in-flight request still returns early instead of resetting state mid-request.
        // A real sold-out response still sets this.error = true further down and still
        // leaves the button disabled, which is correct.
        this.error = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

              if (response.items) {
                setTimeout(() => {
                  if (window.dataLayer) {
                    response.items.forEach(function(item) {
                      window.dataLayer.push({
                        'event': 'add_to_cart',
                        'ecommerce': {
                          'currency': response.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
                          'value': (item.final_line_price || item.line_price || 0) / 100,
                          'items': [{
                            'item_id': item.sku || String(item.product_id),
                            'item_name': item.product_title || item.title,
                            'item_brand': item.vendor || '',
                            'item_category': item.product_type || '',
                            'price': (item.final_price || item.price || 0) / 100,
                            'quantity': item.quantity || 1
                          }]
                        }
                      });
                    });
                  }

                  if (typeof fbq === 'function') {
                    response.items.forEach(function(item) {
                      fbq('track', 'AddToCart', {
                        content_ids: [item.sku || String(item.product_id)],
                        content_type: 'product',
                        value: (item.final_line_price || item.line_price || 0) / 100,
                        currency: response.currency || 'USD'
                      });
                    });
                  }
                }, 0);
              }

              // ChatGPT Ads pixel (snippets/dlux-openai-pixel.liquid). A single-id add
              // returns the line item itself with no `items` array, so the block above
              // never runs for this theme's forms; handle both shapes here.
              const addedItems = response.items || (response.variant_id ? [response] : []);
              if (typeof window.oaiq === 'function' && addedItems.length) {
                setTimeout(() => {
                  addedItems.forEach(function(item) {
                    try {
                      const unit = item.final_price || item.price || 0;
                      const qty = item.quantity || 1;
                      const currency = response.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
                      window.oaiq('measure', 'items_added', {
                        type: 'contents',
                        amount: item.final_line_price || item.line_price || unit * qty,
                        currency: currency,
                        contents: [{
                          id: item.sku || String(item.product_id),
                          name: item.product_title || item.title,
                          content_type: 'product',
                          quantity: qty,
                          amount: unit
                        }]
                      });
                    } catch (e) {}
                  });
                }, 0);
              }
            }
            this.error = false;
            if (this.cart && response.item_count > 0 && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
